export enum Environment {
  Local = 'local',
  Dev = 'dev',
  Int = 'int',
  QA = 'qa',
  UAT = 'uat',
  SuppUAT = 'suppuat',
  Prod = 'prod',
  DR = 'dr',
  Demo = 'demo',
}

const appEnvDomainPrefixes = {
  'suppuat.': Environment.SuppUAT,
  'suppuat-2.': Environment.SuppUAT,
  localhost: Environment.Local,
  local: Environment.Local,
  '127.0.0.1': Environment.Local,
  'dev.': Environment.Dev,
  'dev-2.': Environment.Dev,
  'int.': Environment.Int,
  'int-2.': Environment.Int,
  'qa.': Environment.QA,
  'qa-2.': Environment.QA,
  'uat.': Environment.UAT,
  'uat-2.': Environment.UAT,
  'app.': Environment.Prod,
  'dr.': Environment.DR,
  'demo.': Environment.Demo,
};

export function getAppEnvironment(domain = window.location.hostname): Environment {
  const devDomain = Object.entries(appEnvDomainPrefixes).find(([prefix]) => domain.includes(prefix))?.[1];
  if (devDomain) {
    return devDomain;
  }
  return Environment.Prod;
}

export function isProductionEnvironment(domain = window.location.hostname): boolean {
  const env = getAppEnvironment(domain);
  return env === Environment.Prod || env === Environment.DR;
}

export function isLocalEnvironment(domain = window.location.hostname): boolean {
  const env = getAppEnvironment(domain);
  return env === Environment.Local;
}

const LIO_EMAIL_DOMAINS = ['@lioinsurance.com', '@liospecialty.com'];

export function isLioEmailDomain(email: string | null | undefined): boolean {
  if (!email) return false;
  const lower = email.toLowerCase();
  return LIO_EMAIL_DOMAINS.some((d) => lower.endsWith(d));
}

export const DOMAIN_ACTION_BLOCKED_TOOLTIP =
  'Sign in with a @lioinsurance.com or @liospecialty.com account to use this action.';

export const appConfig = {
  version: process.env.VITE_APP_VERSION,
};

const oktaConfigByEnv = {
  // DR is same as Production
  [Environment.DR]: {
    issuer: 'https://auth.mylioinsurance.com/oauth2/aus90kind6OBLkkYg5d6',
    baseUrl: 'https://auth.mylioinsurance.com',
    clientId: '0oa4q42cacG3ixJOZ5d7',
  },
  [Environment.Prod]: {
    issuer: 'https://auth.mylioinsurance.com/oauth2/aus90kind6OBLkkYg5d6',
    baseUrl: 'https://auth.mylioinsurance.com',
    clientId: '0oa4q42cacG3ixJOZ5d7',
  },
  [Environment.UAT]: {
    issuer: 'https://auth-uat.mylioinsurance.com/oauth2/ausimh2cmtkPGNYTI1d7',
    baseUrl: 'https://auth-uat.mylioinsurance.com',
    clientId: '0oai74uygnWYRogfC1d7',
  },
  // Demo is the same as UAT
  [Environment.Demo]: {
    issuer: 'https://auth-uat.mylioinsurance.com/oauth2/ausimh2cmtkPGNYTI1d7',
    baseUrl: 'https://auth-uat.mylioinsurance.com',
    clientId: '0oai74uygnWYRogfC1d7',
  },
  [Environment.SuppUAT]: {
    issuer: 'https://auth-uat.mylioinsurance.com/oauth2/ausimh2cmtkPGNYTI1d7',
    baseUrl: 'https://auth-uat.mylioinsurance.com',
    clientId: '0oai74uygnWYRogfC1d7',
  },
  default: {
    issuer: 'https://auth-qa.mylioinsurance.com/oauth2/ausikztp2z2auRG9V1d7',
    baseUrl: 'https://auth-qa.mylioinsurance.com',
    // LIO Portal 2 - Dev, Int, QA
    clientId: '0oai72nn0t2gZmvYJ1d7',
  },
};

export const datadogConfig = {
  site: 'datadoghq.com',
  service: 'lio-portal-2',
  logsClientToken: 'pub7a9d4eb4cd5aee8fa55b3bcafe2f2ab2',
  rumApplicationId: '92e27f13-6c45-4ba4-ae96-76e29cf45c83',
  rumClientToken: 'pub0e6229728a171100137e34d5a4788647',
};

export const smartyConfig = {
  key: '17117971461292786',
  license: 'license=us-autocomplete-pro-cloud',
};

const recaptchaConfigByEnv = {
  // not currently env based
  default: {
    siteKey: import.meta.env.VITE_GOOGLE_RECAPTCHA_SITE_KEY,
  },
};

export function getRecaptchaConfig() {
  return {
    siteKey: recaptchaConfigByEnv.default.siteKey,
  };
}

export function getOktaAuthConfig(env: Environment) {
  const envConfig = oktaConfigByEnv[env as keyof typeof oktaConfigByEnv] ?? oktaConfigByEnv.default;
  return {
    issuer: envConfig.issuer,
    clientId: envConfig.clientId,
    redirectUri: `${window.location.origin}/login/callback`,
    postLogoutRedirectUri: `${window.location.origin}/`,
    scopes: ['openid', 'email', 'profile'],
  };
}

export function getOktaSignInConfig(
  env: Environment,
  email?: string,
  goToSignUp?: () => void,
  otp?: string | null,
  state?: string | null,
) {
  const envConfig = oktaConfigByEnv[env as keyof typeof oktaConfigByEnv] ?? oktaConfigByEnv.default;
  return {
    baseUrl: envConfig.baseUrl,
    clientId: envConfig.clientId,
    redirectUri: `${window.location.origin}/login/callback`,
    disableHttpsCheck: true,
    username: email,
    ...(otp ? { otp } : {}),
    ...(state ? { state } : {}),
    authParams: {
      issuer: envConfig.issuer,
      scopes: ['openid', 'email', 'profile'],
    },
    i18n: {
      en: {
        'password.forgot.emailSent.desc': 'An email has been sent to {0} with instructions on resetting your password.',
        'passwordreset.title': 'Reset Password',
        'primaryauth.title': 'Sign In',
        'primaryauth.submit': 'Sign In',
        forgotpassword: 'Forgot password',
        'registration.signup.label': `Don't have an account? `,
        'registration.form.submit': 'Create Account',
        'oktaverify.numberchallenge.instruction': 'On your phone, tap on Okta Verify prompt to continue.',
        'socialauth.divider.text': 'or',

        // verify with your password (second page to login)
        'oie.password.challenge.title': 'Sign In',

        // email verification screen

        // this is used on verify with password, verify with a code, all of the mfa submit actions
        'mfa.challenge.verify': 'Continue',
        // home page primary
        'oform.next': 'Continue',

        // verify mfa with push
        'oie.okta_verify.push.sent': 'Push notification sent',
        'oie.okta_verify.push.resend': 'Resend push notification',

        // select option text
        'oie.verify.authenticator.button.text': 'Select this option',
      },
    },
    customButtons: [
      {
        title: 'Create an account',
        className: 'btn-customAuth',
        click: () => {
          goToSignUp?.();
        },
      },
    ],
  };
}
